import { MenuItemResponse, MenuItemType, Unit } from 'api/models';
import { getBaseUrl } from 'config/apiUtils';
import { TFunction } from 'i18next';
import { TXT_UNIT_GRAM, TXT_UNIT_PIECE } from 'translations';

/** A combo menu item has no single product — it is defined by its component list. */
export const isCombo = (item: MenuItemResponse): boolean =>
  item.itemType === MenuItemType.Combo;

/** Display name: the product name for a regular item, the composition label for a combo. */
export const menuItemName = (item: MenuItemResponse): string =>
  isCombo(item) ? item.compositionLabel ?? '' : item.product?.name ?? '';

type ImageSource = { imageId?: number; imageUrl?: string };

/** Resolves a dish image: the uploaded document if any, otherwise the external URL. */
const imageSourceUrl = ({
  imageId,
  imageUrl,
}: ImageSource): string | undefined =>
  (imageId ? `${getBaseUrl()}/images/${imageId}` : undefined) ??
  imageUrl ??
  undefined;

export const menuItemImageUrl = (item: MenuItemResponse): string | undefined =>
  imageSourceUrl({
    imageId: item.product?.image?.id,
    imageUrl: item.product?.imageUrl,
  });

/** Image urls of a dish: one for a regular item, one per component for a combo. */
export const menuItemImages = (item: MenuItemResponse): string[] =>
  (isCombo(item)
    ? (item.components ?? []).map(imageSourceUrl)
    : [menuItemImageUrl(item)]
  ).filter((url): url is string => !!url);

const UNIT_LABEL: Record<Unit, string> = {
  [Unit.Gram]: TXT_UNIT_GRAM,
  [Unit.Piece]: TXT_UNIT_PIECE,
};

/** The unit a regular item is measured in; a combo is not measured at all. */
export const menuItemUnit = (item: MenuItemResponse): Unit | undefined =>
  isCombo(item) ? undefined : item.product?.unit;

/**
 * Portion of a regular item as a label ("300 гр"), or `undefined` for a combo.
 * `multiplier` scales it to a whole cart line: `menuItemPortionLabel(item, t, 2)` → "600 гр".
 */
export const menuItemPortionLabel = (
  item: MenuItemResponse,
  t: TFunction,
  multiplier = 1,
): string | undefined => {
  const unit = menuItemUnit(item);
  const quantity = item.product?.quantity;

  return unit && quantity
    ? `${Math.round(quantity * multiplier)} ${t(UNIT_LABEL[unit])}`
    : undefined;
};

/** Combos are indivisible (whole numbers only); regular items follow their product. */
export const isMenuItemDivisible = (item: MenuItemResponse): boolean =>
  !isCombo(item) && !!item.product?.isDivisible;
